/// Runtime aircraft model. Owns position, heading, altitude, speed and
/// the per-frame integration of those quantities. Direct port of the
/// flight-critical fields from `struct Aircraft` (Models.swift:553-643)
/// plus the physics loop from `GameScene.swift:870-977` and the
/// altitude integrator at `GameViewModel.swift:2330-2370`.

import type {
  AircraftGoal,
  AircraftType,
  ProcedureLeg,
  Runway,
} from '../types/LevelData'
import { performanceFor } from './AircraftDatabase'

/// Active SID/STAR plan attached to an aircraft. Walked one leg at
/// a time by `tickFlightPlan`. Mirrors iOS's `class FlightPlan`.
export interface FlightPlan {
  procedureName: string
  legs: ProcedureLeg[]
  /// Index of the leg the aircraft is currently flying *to*. Starts
  /// at 0; each leg-crossing advances by 1; reaching `legs.length`
  /// completes the procedure.
  currentLegIndex: number
}

/// `awaitingTakeoff` and `takingOff` are ground/early-air states for
/// goal=takeoff aircraft; `completed` is a terminal "off-radar" state
/// used for transition aircraft after they cross their exit fix.
export type AircraftStatus =
  | 'enRoute'
  | 'landing'
  | 'landed'
  | 'awaitingTakeoff'
  | 'takingOff'
  | 'completed'

export type EmergencyReason =
  | 'birdStrike'
  | 'lowFuel'
  | 'engineFailure'
  | 'vfrInIfr'
  | 'medicalEmergency'
  | 'pressureLoss'

export interface EmergencyState {
  reason: EmergencyReason
  /// Seconds remaining until the aircraft is "lost". Decremented each
  /// tick by GameSession; the autopilot doesn't read this directly.
  countdownSec: number
}

export interface AircraftInit {
  id: string
  flightNumber: string
  type: AircraftType
  goal: AircraftGoal
  position: { x: number; y: number }
  headingDeg: number
  altitudeFt: number
  speedKts: number
  status?: AircraftStatus
  /// Initial directTo target (for transition / takeoff goals where we
  /// want the autopilot steering toward a fix from spawn).
  directToPoint?: { x: number; y: number }
  directToName?: string | null
  /// Recorded for takeoff aircraft so the UI/log can surface context.
  departureRunway?: { airportName: string; runwayId: string } | null
  /// 4-digit octal transponder squawk (digits 0–7). Cosmetic on
  /// the data tag and flight strip. Emergency aircraft are forced
  /// to "7700" by GameSession when the emergency rolls. Optional
  /// at construction time — production code always supplies via
  /// `GameSession.nextSquawk`; tests fall through to the placeholder.
  squawk?: string
  /// True when this aircraft flies itself (the AI controller drives it).
  aiControlled?: boolean
}

export class Aircraft {
  readonly id: string
  readonly flightNumber: string
  readonly type: AircraftType
  readonly goal: AircraftGoal

  position: { x: number; y: number }
  headingDeg: number
  targetHeadingDeg: number
  /// How the autopilot should turn from `headingDeg` to
  /// `targetHeadingDeg`. `shortest` (default) takes the smaller arc;
  /// `left` and `right` force the indicated direction even when it's
  /// the long way around (e.g., a 350°→010° "Turn left" arcs through
  /// 270°). Auto-resets to `shortest` once the target is reached.
  commandedTurnDirection: 'shortest' | 'left' | 'right' = 'shortest'
  altitudeFt: number
  targetAltitudeFt: number
  speedKts: number
  /// Set by takeoff command: ramps speedKts → targetSpeedKts at
  /// performanceFor(type).takeoffAccelRate. For non-takeoff aircraft
  /// equal to speedKts (no acceleration model in the MVP).
  targetSpeedKts: number
  status: AircraftStatus = 'enRoute'

  selected = false

  /// Set when cleared-to-land OR for transition-toward-fix steering.
  /// The autopilot uses this to drive the heading each tick.
  directToPoint: { x: number; y: number } | null = null
  /// Human-readable name of the directTo target (waypoint name or
  /// "RWY 22") — used by goal-completion logic and the UI.
  directToName: string | null = null
  /// True when the AI controller flies this aircraft (no player input).
  aiControlled = false
  /// AI-locked landing runway (the chosen runway's id1 + approach heading) —
  /// set once so the AI doesn't oscillate between ends as the wind drifts.
  aiRunway: { runwayId1: string; approachHeadingDeg: number } | null = null
  /// Runway being landed on (used to look up the target/elevation).
  landingRunway: Runway | null = null
  /// Approach heading the player was cleared to land on. Stored so
  /// `tickLanding` can re-project the threshold each frame from
  /// the live runway endpoints — necessary for moving airports
  /// (carriers) where the threshold position drifts every tick.
  /// `null` for aircraft not currently on a landing clearance.
  landingApproachHeadingDeg: number | null = null
  /// ft/min override for descent during landing — calculated from
  /// distance/time-to-runway by `Autopilot.issueLandingClearance`.
  descentRateOverride: number | null = null
  /// For takeoff aircraft, the runway they're departing from.
  departureRunway: { airportName: string; runwayId: string } | null = null
  /// Set when "Cleared for approach" is active. The autopilot
  /// tracks the runway extended centerline + locks heading to the
  /// approach heading; altitude stays under player control until
  /// they issue "Cleared to land".
  approachIntercept: {
    airportName: string
    runwayId1: string
    runwayId2: string
    /// The runway-end ID the aircraft is actively approaching —
    /// either id1 or id2, picked at clearance time based on the
    /// active runway. Drives the "established RWY 22" readback.
    activeRunwayId: string
    approachHeadingDeg: number
    thresholdPx: { x: number; y: number }
    /// True the first frame the autopilot finds itself within the
    /// on-localizer band — flips on once and stays on. Used to fire
    /// the one-shot "established" check-in readback without it
    /// re-firing every time perpNM oscillates back across 0.3 NM.
    established: boolean
  } | null = null
  /// Active racetrack hold pattern. Non-null while the aircraft is
  /// flying the hold; cleared by Cancel hold, by setHeading, by a
  /// direct-to to a different fix, or by Cleared to land.
  ///
  /// Phases (FAA-style right-turn racetrack):
  ///   - `flyingToWaypoint`: en route to the fix on the inbound heading.
  ///   - `firstTurn`: 180° right turn from inbound to outbound.
  ///   - `outboundLeg`: 60-second straight leg on the outbound heading.
  ///   - `secondTurn`: 180° right turn from outbound back to inbound.
  /// On completion of secondTurn, we re-enter `flyingToWaypoint` and
  /// the loop repeats indefinitely.
  holdingState: {
    fixName: string
    fixPoint: { x: number; y: number }
    /// Bearing to the fix at the moment Hold was issued. Locked for
    /// the lifetime of the hold so the racetrack stays on the same
    /// orientation regardless of where the aircraft drifts.
    inboundHeadingDeg: number
    phase: 'flyingToWaypoint' | 'firstTurn' | 'outboundLeg' | 'secondTurn'
    outboundTimerSec: number
    /// One-shot guard so the +100 "entered hold" bonus only fires
    /// the first time the aircraft reaches the fix.
    hasScored: boolean
  } | null = null
  /// Active SID/STAR flight plan, or null. Drives multi-leg
  /// procedure flying — set on takeoff for departure goals and at
  /// spawn for arrival goals; cleared when the last leg is reached
  /// or the player vectors out of the procedure (setHeading).
  flightPlan: FlightPlan | null = null
  /// Remaining hold time (sim-seconds) for a `survey` goal aircraft
  /// that has reached its circle waypoint. Non-null while the auto-
  /// engaged hold is ticking; cleared by player overrides (setHeading,
  /// cancel hold, direct-to a different fix) or by `tickSurvey` when
  /// the timer expires and the aircraft is routed to its exit fix.
  surveyHoldRemainingSec: number | null = null
  /// Count of altitude restrictions busted along the current
  /// procedure. Each leg crossing checks `altitudeFt` against the
  /// leg's `altitudeRestriction`; if above, this counter ticks.
  /// Surfaced in the EndScreen breakdown via the 'violations'
  /// score category.
  procedureViolations: number = 0
  /// Sim seconds the aircraft has been on radar — incremented every
  /// tick GameSession runs while status ∈ {takingOff, enRoute,
  /// landing}. Drives the time-on-radar bonus that's awarded when
  /// the aircraft completes its goal: faster handle = more points.
  /// `awaitingTakeoff` doesn't accumulate (the controller hasn't
  /// started handling the aircraft yet); `landed`/`completed` are
  /// terminal so they don't accumulate either.
  radarTimeSec: number = 0
  /// Active emergency, if any. Aircraft that touch down while this is
  /// non-null collect a bonus; if the countdown expires they fail.
  emergency: EmergencyState | null = null
  /// Result of the last collision-detector pass: 'normal' (no
  /// proximity), 'conflict' (within 2 NM/500 ft of another aircraft),
  /// 'terrainWarning' (within 2 NM/300 ft of high terrain), or
  /// 'crashed' (terminal). Reset to 'normal' at the start of each
  /// detection sweep so old conflicts clear.
  collisionStatus: 'normal' | 'conflict' | 'terrainWarning' | 'crashed' = 'normal'
  /// Human-readable detail for the current collision state — the
  /// other aircraft's callsign for 'conflict', a terrain peak label
  /// for 'terrainWarning', or the crash reason for 'crashed'.
  collisionDetail: string | null = null
  /// 4-digit octal squawk. Emergency aircraft are stamped to
  /// "7700" by GameSession on emergency-roll.
  squawk: string

  constructor(init: AircraftInit) {
    this.id = init.id
    this.flightNumber = init.flightNumber
    this.type = init.type
    this.goal = init.goal
    this.squawk = init.squawk ?? '0000'
    this.aiControlled = init.aiControlled ?? false
    this.position = { ...init.position }
    this.headingDeg = init.headingDeg
    this.targetHeadingDeg = init.headingDeg
    this.altitudeFt = init.altitudeFt
    this.targetAltitudeFt = init.altitudeFt
    this.speedKts = init.speedKts
    this.targetSpeedKts = init.speedKts
    if (init.status) this.status = init.status
    if (init.directToPoint) this.directToPoint = { ...init.directToPoint }
    if (init.directToName !== undefined) this.directToName = init.directToName
    if (init.departureRunway !== undefined) this.departureRunway = init.departureRunway
  }

  /// Advance state by `dt` seconds. `pxPerNM` is the world-pixel scale
  /// (consistent across the level); used to convert speed into screen
  /// motion.
  tick(dt: number, pxPerNM: number): void {
    if (this.status === 'landed') return

    // `completed` aircraft (overflew their exit fix) keep drifting on
    // their last heading until the GameSession boundary sweep removes
    // them — otherwise they freeze on top of the fix.
    if (this.status === 'completed') {
      this.advancePosition(dt, pxPerNM)
      return
    }

    this.advanceHeading(dt)
    this.advanceSpeed(dt)

    // Aircraft sitting on the runway awaiting a takeoff command don't
    // move and don't change altitude — they're parked at the threshold.
    if (this.status !== 'awaitingTakeoff') {
      this.advanceAltitude(dt)
      this.advancePosition(dt, pxPerNM)
    }
  }

  private advanceHeading(dt: number): void {
    if (this.headingDeg === this.targetHeadingDeg) {
      // Latch back to shortest once the commanded turn finishes,
      // so the next heading the player issues uses the default
      // unless they explicitly force a side again.
      if (this.commandedTurnDirection !== 'shortest') {
        this.commandedTurnDirection = 'shortest'
      }
      return
    }
    // Per-type turn rate. Fixed-wing = 3°/s (~standard rate);
    // helicopters yaw on the spot at 6°/s. On the ground we don't
    // turn.
    if (this.status === 'awaitingTakeoff') return
    const turnRate = performanceFor(this.type).turnRateDegSec
    // Signed shortest-arc delta in (−180, 180].
    const shortestDiff = ((this.targetHeadingDeg - this.headingDeg + 540) % 360) - 180
    // Effective remaining arc + sign, after honoring a forced turn
    // direction. For 'left' (negative direction), if the shortest
    // arc is positive we take the long way around (360 − |arc|).
    // Same in mirror for 'right'.
    let signedRemaining: number
    if (this.commandedTurnDirection === 'left') {
      signedRemaining = shortestDiff <= 0 ? shortestDiff : shortestDiff - 360
    } else if (this.commandedTurnDirection === 'right') {
      signedRemaining = shortestDiff >= 0 ? shortestDiff : shortestDiff + 360
    } else {
      signedRemaining = shortestDiff
    }
    const step = turnRate * dt
    if (Math.abs(signedRemaining) <= step) {
      this.headingDeg = this.targetHeadingDeg
      this.commandedTurnDirection = 'shortest'
    } else {
      this.headingDeg = wrap360(this.headingDeg + Math.sign(signedRemaining) * step)
    }
  }

  private advanceSpeed(dt: number): void {
    if (this.speedKts === this.targetSpeedKts) return
    const perf = performanceFor(this.type)
    // Per-type rates: takeoffAccelRate during the ground roll +
    // climbout, normalLandingDecelRate (which doubles as the cruise
    // accel/decel value) for everything else. Heavies slow much
    // more slowly than fighters / helicopters — that's what makes
    // a 747 feel like a 747 on approach.
    const ratePerSec =
      this.status === 'takingOff' ? perf.takeoffAccelRate : perf.normalLandingDecelRate
    const step = ratePerSec * dt
    const diff = this.targetSpeedKts - this.speedKts
    if (Math.abs(diff) <= step) {
      this.speedKts = this.targetSpeedKts
    } else {
      this.speedKts += Math.sign(diff) * step
    }
  }

  private advanceAltitude(dt: number): void {
    if (this.altitudeFt === this.targetAltitudeFt) return
    let ratePerMinute: number
    if (this.descentRateOverride != null && this.status === 'landing') {
      ratePerMinute = this.descentRateOverride
    } else {
      const perf = performanceFor(this.type)
      ratePerMinute =
        this.targetAltitudeFt > this.altitudeFt ? perf.normalClimbRate : perf.normalDescentRate
    }
    const ratePerSecond = ratePerMinute / 60
    const step = ratePerSecond * dt
    const diff = this.targetAltitudeFt - this.altitudeFt
    if (Math.abs(diff) <= step) {
      this.altitudeFt = this.targetAltitudeFt
    } else {
      this.altitudeFt += Math.sign(diff) * step
    }
  }

  private advancePosition(dt: number, pxPerNM: number): void {
    if (this.speedKts <= 0) return
    // speed in NM/sec → pixels/sec. Heading 0° = N (screen up = -y).
    const pxPerSec = (this.speedKts / 3600) * pxPerNM
    // Convert magnetic heading to screen-Y-down angle:
    //   heading=0 (N) → screenAngle = -90° → cos=0, sin=-1.
    const screenAngle = ((this.headingDeg - 90) * Math.PI) / 180
    this.position = {
      x: this.position.x + Math.cos(screenAngle) * pxPerSec * dt,
      y: this.position.y + Math.sin(screenAngle) * pxPerSec * dt,
    }
  }
}

function wrap360(d: number): number {
  let v = d % 360
  if (v < 0) v += 360
  return v
}
